/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.16.0
 * @since 1.16.0
 */

import { useCallback } from 'react';

import { useDropzone } from 'react-dropzone';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { Button, InfoBlock } from '../../../components/common';
import { ImgStyled } from '../../../components/common/Block/Img';
import { ELinkTarget } from '../../../components/common/ExternalLink/ExternalLink';
import LinkBlock from '../../../components/common/LinkBlock/LinkBlock';
import { EColorStatus, EFileExtension, MapExtensionToIcon } from '../../../modules/app/appConstants';
import { makeSelectTranslationWizardImportPersonalData } from '../../../modules/internationalization/intSelectors';
import { IWizardImportPersonalDataTranslation } from '../../../modules/internationalization/types/wizards/importPersonalData';
import { IRootReducer } from '../../../reducers/rootReducer';
import { getFileExtension } from '../../../utils/formatText';
import { stringFormatParams } from '../../../utils/stringFormat';
import { IWizardImportPersonalDataAttachFileActionPayload } from '../types/wizardImportPersonalDataActionTypes';
import { wizardImportPersonalDataAttachFile } from '../wizardImportPersonalDataActions';
import { EWizardImportPersonalDataSteps } from '../wizardImportPersonalDataConstant';
import { IWizardImportPersonalDataReducer } from '../wizardImportPersonalDataReducer';
import {
    makeSelectWizardImportPersonalDataAttachFileErrorCode,
    makeSelectWizardImportPersonalDataFileName,
    makeSelectWizardImportPersonalDataFileTemplateUrl,
    makeSelectWizardImportPersonalDataMaxImportSubscriberAmount,
} from '../wizardImportPersonalDataSelectors';
import { WizardImportPersonalDataStyled } from './styled/WizardImportPersonalDataStyled';

const WizardImportPersonalDataDocumentSelectionStep = (props: IConnectedProps & IDispatchProps) => {
    const {
        errorCode,
        fileName,
        fileTemplateUrl,
        maxImportSubscriberAmount,
        translation,

        attachFile,
    } = props;

    const stepTranslation = translation[EWizardImportPersonalDataSteps.DocumentSelectionStep];

    const onDropHandler = useCallback((acceptedFiles: File[]) => {
        attachFile({
            file: acceptedFiles[0],
        });
    }, [attachFile]);

    const { getInputProps, open } = useDropzone({
        onDrop: onDropHandler,
        multiple: false,
        noClick: false,
        noKeyboard: false,
    });

    return <WizardImportPersonalDataStyled>
        {/* Hidden input */}
        <input
            {...getInputProps()}
            data-name="wizard-import-personal-data-choose-file-input"
        />
        <div className="wizard-import-personal-data__title">{stepTranslation.title}</div>
        <InfoBlock className="wizard-import-personal-data__warning" colorStatus={EColorStatus.WARNING}>
            <div dangerouslySetInnerHTML={{
                __html: stepTranslation.warning,
            }}/>
        </InfoBlock>
        <div>
            {stepTranslation.template}&nbsp;
            {
                fileTemplateUrl
                    ? <LinkBlock
                        isExternalUrl
                        dataName="import-personal-data-file-template-url"
                        target={ELinkTarget.BLANK}
                        url={fileTemplateUrl}
                    >
                        {stepTranslation.templateUrl}
                    </LinkBlock>
                    : stepTranslation.templateUrl
            }
        </div>
        <div>
            {stringFormatParams(stepTranslation.maxImportSubs, { maxSubs: maxImportSubscriberAmount })}
        </div>
        <div dangerouslySetInnerHTML={{
            __html: stringFormatParams(stepTranslation.info, { maxSubs: maxImportSubscriberAmount }),
        }}/>
        {
            fileName &&
            <div className="wizard-import-personal-data__attach-file">
                <ImgStyled
                    className="medium"
                    src={MapExtensionToIcon[getFileExtension(fileName) as EFileExtension]
                    ?? icons.defaultFileIcon}
                />
                <div className="wizard-import-personal-data__attach-file-name">{fileName}</div>
            </div>
        }
        {
            !fileName && errorCode &&
            <div className="wizard-import-personal-data__attach-file-error">
                {stringFormatParams(stepTranslation.attachError.codes[errorCode],
                    { maxSubs: maxImportSubscriberAmount })}
            </div>
        }
        <Button
            className="wizard-import-personal-data__button"
            dataName="wizard-import-personal-data-choose-file-btn"
            kind="medium"
            onClick={open}
        >
            {stepTranslation.chooseFile}
        </Button>
    </WizardImportPersonalDataStyled>;
};

export interface IConnectedProps extends Pick<IWizardImportPersonalDataReducer,
    'errorCode' |
    'fileName' |
    'fileTemplateUrl' |
    'maxImportSubscriberAmount'>
{
    translation: IWizardImportPersonalDataTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    errorCode: makeSelectWizardImportPersonalDataAttachFileErrorCode(),
    fileName: makeSelectWizardImportPersonalDataFileName(),
    fileTemplateUrl: makeSelectWizardImportPersonalDataFileTemplateUrl(),
    maxImportSubscriberAmount: makeSelectWizardImportPersonalDataMaxImportSubscriberAmount(),
    translation: makeSelectTranslationWizardImportPersonalData(),
});

export interface IDispatchProps {
    attachFile: (payload: IWizardImportPersonalDataAttachFileActionPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    attachFile: (payload: IWizardImportPersonalDataAttachFileActionPayload) =>
        dispatch(wizardImportPersonalDataAttachFile(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps) (WizardImportPersonalDataDocumentSelectionStep);
